import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AttachmentModel, ConciergeItemModel } from 'src/models/concierge.model';
import { Aws } from 'src/providers/aws/aws';
import { generateUuid } from 'src/providers/tidy-session/session-uuid';
import { FilePicker } from 'src/shared/providers/file-picker/file-picker';
import { Util } from 'src/shared/util/util';

export interface ConciergeItemsResponse {
  body: ConciergeItemModel[];
  totalRecords: string | null;
}

export interface CreateConciergeItemData {
  type: string;
  data: {
    subject: string;
    body: string;
    attachments: string[];
    emails: string[];
    scheduled?: string;
  };
  metadata?: Record<string, unknown>;
}

export interface TicketCommentData {
  support_ticket_id: number;
  content: string;
  attachments: string[];
  is_ai_response?: boolean;
}

export interface ActionTakenFeedbackData {
  action_taken_key: string;
  action_object_id: number;
  feedback_status: 'approved' | 'rejected';
  feedback_text?: string;
  metadata?: Record<string, unknown>;
}

export interface ConciergeRule {
  id: number;
  name: string;
  [key: string]: unknown;
}

export type ConciergeSuggestionStatus = 'pending' | 'approved' | 'rejected';

export interface ConciergeSuggestion {
  id: number;
  title?: string;
  description?: string;
  status?: ConciergeSuggestionStatus;
  address_id?: number | null;
  dismissed_at?: string | null;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class ConciergeApiService {
  private readonly http = inject(HttpClient);
  private readonly aws = inject(Aws);
  private readonly filePicker = inject(FilePicker);
  private readonly util = inject(Util);
  private readonly baseUrl = environment.api_url;

  getConciergeItems(filters = ''): Observable<ConciergeItemsResponse> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items?visible=true${filters}&not_created_by=Genome`;
    return this.http.get<ConciergeItemModel[]>(url, { observe: 'response' }).pipe(
      map((response: HttpResponse<ConciergeItemModel[]>) => ({
        body: (response.body || []).filter(item => item.created_by !== 'Genome'),
        totalRecords: response.headers.get('X-Total-Records')
      }))
    );
  }

  // Deciding a dispute marks its initiated and counter-answer items done and adds a
  // won/lost item that usually stays open, so only these two open types mean the
  // dispute is still in progress. The API filters on `done`, not `is_marked_done`.
  getInProgressDispute(): Observable<ConciergeItemModel | null> {
    const filters = '&concierge_item_type_key=customer_dispute_initiated,customer_dispute_counter_answer&done=false&per_page=1';
    return this.getConciergeItems(filters).pipe(
      map(response => response.body[0] ?? null)
    );
  }

  getConciergeItemById(conciergeItemId: number): Observable<ConciergeItemModel> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/${conciergeItemId}?includes=agent_info,recent_comments&comments_limit=50`;
    return this.http.get<ConciergeItemModel>(url);
  }

  getConciergeItemComments(conciergeItemId: number, page = 1, perPage = 50): Observable<{
    comments: any[];
    totalRecords: number;
  }> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/${conciergeItemId}/comments?page=${page}&per_page=${perPage}`;
    return this.http.get<any[]>(url, { observe: 'response' }).pipe(
      map(response => ({
        comments: response.body || [],
        totalRecords: parseInt(response.headers.get('X-Total-Records') || '0', 10)
      }))
    );
  }

  createConciergeItem(data: CreateConciergeItemData): Observable<ConciergeItemModel> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items`;
    return this.http.post<ConciergeItemModel>(url, data);
  }

  updateConciergeItem(itemId: number, data: Record<string, unknown>): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/${itemId}/action`;
    return this.http.post(url, data);
  }

  updateConciergeItemSubject(itemId: number, subject: string): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/${itemId}/action`;
    return this.http.post(url, {
      action_name: 'update_request_data',
      params: { subject }
    });
  }

  closeSupportRequest(itemId: number): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/${itemId}/action`;
    return this.http.post(url, {
      action_name: 'mark_ticket_as_closed'
    });
  }

  addTicketComment(data: TicketCommentData): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/support/ticket-comments`;
    return this.http.post(url, data);
  }

  recordUserComment(conciergeItemId: number, content: string): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/ai-agent-tools/execute`;
    return this.http.post(url, {
      tool_name: 'concierge_item.add_comment',
      params: { concierge_item_id: conciergeItemId, content },
    });
  }

  submitFeedback(data: ActionTakenFeedbackData): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/action-taken-user-feedbacks`;
    return this.http.post(url, data);
  }

  replyDisputeCounter(conciergeItemId: number, data: Record<string, unknown>): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/${conciergeItemId}/action`;
    return this.http.post(url, data);
  }

  openDispute(data: Record<string, unknown>): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/cleaning-disputes`;
    return this.http.post(url, data);
  }

  getRules(): Observable<ConciergeRule[]> {
    const url = `${this.baseUrl}api/v1/customer/concierge-rules`;
    return this.http.get<ConciergeRule[]>(url);
  }

  addRule(data: Record<string, unknown>): Observable<ConciergeRule> {
    const url = `${this.baseUrl}api/v1/customer/concierge-rules`;
    return this.http.post<ConciergeRule>(url, data);
  }

  updateRule(ruleId: number, data: Record<string, unknown>): Observable<ConciergeRule> {
    const url = `${this.baseUrl}api/v1/customer/concierge-rules/${ruleId}`;
    return this.http.put<ConciergeRule>(url, data);
  }

  deleteRule(ruleId: number): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-rules/${ruleId}`;
    return this.http.delete(url);
  }

  // address_id scopes suggestions to a single property; omit it for the account-wide list.
  getSuggestions(addressId?: number | null): Observable<ConciergeSuggestion[]> {
    let url = `${this.baseUrl}api/v1/customer/concierge-suggestions?not_dismissed=true`;
    if (addressId !== null && addressId !== undefined) {
      url += `&address_id=${addressId}`;
    }
    return this.http.get<ConciergeSuggestion[]>(url);
  }

  approveSuggestion(suggestionId: number): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-suggestions/${suggestionId}/approve`;
    return this.http.post(url, {});
  }

  rejectSuggestion(suggestionId: number): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-suggestions/${suggestionId}/reject`;
    return this.http.post(url, {});
  }

  // Deprecated on the backend (kept for older clients; maps to reject). Prefer rejectSuggestion.
  dismissSuggestion(suggestionId: number): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-suggestions/${suggestionId}/dismiss`;
    return this.http.post(url, {});
  }

  createManyConciergeItems(
    items: CreateConciergeItemData[],
    taskId: string | null = null
  ): Observable<unknown> {
    const url = `${this.baseUrl}api/v1/customer/concierge-items/create-many`;
    const data = {
      concierge_items: items,
      ...(taskId && { issue_report_id: taskId })
    };
    return this.http.post(url, data);
  }

  async addAttachment(): Promise<AttachmentModel> {
    const uuid = generateUuid();
    const file = await this.filePicker.pickFile();
    const fileName = file.filename.replace(/ /g, '');
    const fileKey = `concierge-attachments/${uuid}/${fileName}`;
    if (!file?.format) {
      const dataSource = file?.dataUrl;
      file.format = dataSource.split(',')[0].split('/')[1];
    }
    const loading = await this.util.showLoading();
    try {
      const response = await this.aws.uploadFileToS3(file.dataUrl, fileKey, file.format);
      return {
        filename: fileName,
        fileKey,
        url: response.Location
      };
    } finally {
      loading.dismiss();
    }
  }

  getStatusIcon(status: string): string | undefined {
    switch (status) {
      case 'waiting_on_tidy':
        return 'assets/img/yellow-pending.svg';
      case 'waiting_on_customer':
        return 'assets/img/yellow-alert.svg';
      case 'completed':
      case 'closed':
        return 'assets/img/checkmark.svg';
      default:
        return undefined;
    }
  }

  getDisputeIcon(type: string): string | undefined {
    switch (type) {
      case 'dispute.customer_dispute_initiated':
        return 'assets/img/yellow-pending.svg';
      case 'dispute.customer_dispute_counter_answer':
        return 'assets/img/yellow-alert.svg';
      case 'dispute.customer_dispute_won':
      case 'dispute.customer_dispute_lost':
        return 'assets/img/checkmark.svg';
      default:
        return undefined;
    }
  }

  getReplyTime(type: string): string {
    switch (type) {
      case 'support.other':
        return '2 business days';
      case 'support.how_to':
      case 'support.developer':
      case 'support.integration':
        return '2 business hours';
      case 'support.billing':
        return '4 business hours';
      case 'support.job_issue':
        return '2 business days';
      default:
        return '2 business hours';
    }
  }
}
